import { Alert, Divider, Typography } from '@mui/material';
import { AxiosError } from 'axios';
import { FormikHelpers, FormikProvider, useFormik } from 'formik';
import {
  filter,
  find,
  isEmpty,
  keyBy,
  map,
  some,
  toUpper,
  uniq,
  values,
} from 'lodash';
import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from 'react-query';
import * as Yup from 'yup';

import { currencyExchangeApi, payoutOrdersApi, shopsApi } from 'api';
import {
  CloseFormikDialogResult,
  Dialog,
  DialogProps,
  FormControls,
  FormikNumericField,
  FormikSelect,
  FormikSelectOption,
  FormikTextField,
  ShopSelect,
} from 'components';
import { QueryKey, ShopStatus, WebhookMethod } from 'enums';
import { buildFormValidator, FormFields } from 'features/form-builder';
import {
  useCurrencies,
  useMutation,
  useShopsQuery,
  useUserContext,
  useUserContextUtils,
} from 'hooks';
import { TranslationNamespace } from 'i18n';
import { CreateMerchantPayoutOrderDto, FormField, Shop } from 'types';
import { formUtils, orderUtils } from 'utils';

type Values = CreateMerchantPayoutOrderDto;

const REQUISITES_PREFIX = 'customer.requisites.';

const SUPPORTED_REQUISITE_NAMES = new Set<string>([
  'cardInfo',
  'phone',
  'cardholder',
  'swiftBic',
  'bic',
  'idCard',
  'beneficiaryName',
  'accountNumber',
  'expirationDate',
  'taxId',
]);

type Props = DialogProps;

export const MerchantCreateOrderDialog: React.FC<Props> = ({
  open,
  onClose,
}) => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.orders.create_order_dialog',
  });
  const { t: tCommon } = useTranslation(TranslationNamespace.Common);
  const { tradeMethods, paymentTypes, banks } = useUserContext();
  const { getPaymentTypesOptions, getNotArchived } = useUserContextUtils();
  const queryClient = useQueryClient();
  const { fiatCurrenciesOptions } = useCurrencies();

  const paymentTypesById = useMemo(
    () => keyBy(paymentTypes, 'id'),
    [paymentTypes],
  );

  const initialValues: Values = useMemo(
    () => ({
      amount: 0,
      shopId: '',
      fiatCurrencyId: '',
      paymentTypeId: '',
      bankId: '',
      customer: {
        id: '',
        name: '',
        email: '',
        telegram: '',
        requisites: {},
      },
      integration: {
        externalOrderId: '',
        callbackUrl: '',
        callbackMethod: WebhookMethod.Post,
      },
    }),
    [],
  );

  const queryResultShops = useShopsQuery(
    QueryKey.Shops,
    shopsApi.getAllAsMerchant,
    { enabled: open },
  );
  const queryResultCurrencyExchange = useQuery(
    QueryKey.CurrencyExchangeRates,
    () => currencyExchangeApi.getRates(),
    { enabled: open },
  );

  const filteredShops: Shop[] = useMemo(
    () =>
      filter(
        queryResultShops.data,
        (shop) => !shop.archived && shop.status === ShopStatus.Active,
      ),
    [queryResultShops.data],
  );

  const { mutate: create } = useMutation(payoutOrdersApi.createAsMerchant, {
    onError: (error: AxiosError) => {
      formik.setErrors(orderUtils.getFormErrors(error));
    },
  });

  const handleClose = useCallback(
    (result: CloseFormikDialogResult<Values>) => {
      if (!result.ok) {
        result?.data?.formikHelpers.resetForm();
        onClose(result);
      } else if (result.data?.values) {
        create(result.data.values, {
          onSuccess: () => {
            queryClient.invalidateQueries(QueryKey.ActivePayoutOrders);
            result.data?.formikHelpers.resetForm();
            onClose(result);
          },
        });
      }
    },
    [queryClient, create, onClose],
  );

  const handleSubmit = useCallback(
    (formValues: Values, formikHelpers: FormikHelpers<Values>) => {
      handleClose({ ok: true, data: { values: formValues, formikHelpers } });
    },
    [handleClose],
  );

  const [requisiteFields, setRequisiteFields] = useState<FormField[]>([]);

  const displayedRequisiteFields = useMemo(
    () =>
      map(requisiteFields, (f) => ({
        ...f,
        name: `${REQUISITES_PREFIX}${f.name}`,
      })),
    [requisiteFields],
  );

  const validationSchema: Yup.ObjectSchema<any> = useMemo(() => {
    const requisitesSchema = buildFormValidator(requisiteFields);
    return Yup.object().shape({
      amount: Yup.number()
        .moreThan(0, tCommon('errors.natural_number'))
        .required(tCommon('errors.required')),
      shopId: Yup.string().required(tCommon('errors.required')),
      fiatCurrencyId: Yup.string().required(tCommon('errors.required')),
      paymentTypeId: Yup.string().required(tCommon('errors.required')),
      bankId: Yup.string().required(tCommon('errors.required')),
      customer: Yup.object().shape({
        id: Yup.string().required(tCommon('errors.required')),
        name: Yup.string(),
        email: Yup.string().email(tCommon('errors.email')),
        telegram: Yup.string(),
        requisites: requisitesSchema,
      }),
      integration: Yup.object().shape({
        externalOrderId: Yup.string(),
        callbackUrl: Yup.string(),
        callbackMethod: Yup.string().is(values(WebhookMethod)),
      }),
    });
  }, [tCommon, requisiteFields]);

  const formik = useFormik<Values>({
    initialValues,
    validationSchema,
    validateOnMount: true,
    onSubmit: handleSubmit,
    isInitialValid: false,
  });
  const { setFieldValue, setFieldTouched, validateForm } = formik;

  const queryResultShopPayoutConfig = useQuery(
    [QueryKey.ShopPayoutTradeMethodsConfig, formik.values.shopId],
    () => shopsApi.getPayoutConfigAsMerchant(formik.values.shopId),
    { enabled: open && !!formik.values.shopId },
  );

  const supportedTradeMethods = useMemo(
    () =>
      filter(tradeMethods, (tm) =>
        (queryResultShopPayoutConfig.data?.tradeMethodIds ?? []).includes(
          tm.id,
        ),
      ),
    [tradeMethods, queryResultShopPayoutConfig.data],
  );

  const hasNoPayoutConfig = useMemo(
    () =>
      !!formik.values.shopId &&
      queryResultShopPayoutConfig.isFetched &&
      isEmpty(queryResultShopPayoutConfig.data?.tradeMethodIds),
    [
      formik.values.shopId,
      queryResultShopPayoutConfig.isFetched,
      queryResultShopPayoutConfig.data,
    ],
  );

  const showPayoutSections = useMemo(
    () =>
      !!formik.values.shopId &&
      queryResultShopPayoutConfig.isFetched &&
      !isEmpty(queryResultShopPayoutConfig.data?.tradeMethodIds),
    [
      formik.values.shopId,
      queryResultShopPayoutConfig.isFetched,
      queryResultShopPayoutConfig.data,
    ],
  );

  useEffect(() => {
    const rawFields = formik.values.paymentTypeId
      ? paymentTypesById[formik.values.paymentTypeId]?.fields ?? []
      : [];
    const fields = filter(rawFields, (f) =>
      SUPPORTED_REQUISITE_NAMES.has(f.name),
    );
    setRequisiteFields(fields);
    const visibleNames = new Set(
      filter(fields, (f) => !f.hidden).map((f) => f.name),
    );
    SUPPORTED_REQUISITE_NAMES.forEach((name) => {
      if (!visibleNames.has(name)) {
        setFieldValue(`${REQUISITES_PREFIX}${name}`, undefined, false);
      }
    });
  }, [formik.values.paymentTypeId, paymentTypesById, setFieldValue]);

  useEffect(() => {
    validateForm();
  }, [requisiteFields, validateForm]);

  useEffect(() => {
    if (!queryResultShopPayoutConfig.isFetched) {
      return;
    }
    const allowedPaymentTypeIds = new Set(
      map(supportedTradeMethods, 'paymentTypeId'),
    );
    if (
      formik.values.paymentTypeId &&
      !allowedPaymentTypeIds.has(formik.values.paymentTypeId)
    ) {
      setFieldValue('paymentTypeId', '', false);
      setFieldValue('bankId', '', false);
      return;
    }
    const tradeMethodsForPaymentType = formik.values.paymentTypeId
      ? filter(supportedTradeMethods, {
          paymentTypeId: formik.values.paymentTypeId,
        })
      : supportedTradeMethods;
    const allowedBankIds = new Set(map(tradeMethodsForPaymentType, 'bankId'));
    if (formik.values.bankId && !allowedBankIds.has(formik.values.bankId)) {
      setFieldValue('bankId', '', false);
    }
  }, [
    supportedTradeMethods,
    queryResultShopPayoutConfig.isFetched,
    formik.values.paymentTypeId,
    formik.values.bankId,
    setFieldValue,
  ]);

  const paymentTypesOptions = useMemo(() => {
    const allowedPaymentTypeIds = new Set(
      map(supportedTradeMethods, 'paymentTypeId'),
    );
    return filter(getPaymentTypesOptions(paymentTypes), (opt) =>
      allowedPaymentTypeIds.has(opt.value),
    );
  }, [paymentTypes, getPaymentTypesOptions, supportedTradeMethods]);

  const bankOptions = useMemo(() => {
    const tradeMethodsForPaymentType = formik.values.paymentTypeId
      ? filter(supportedTradeMethods, {
          paymentTypeId: formik.values.paymentTypeId,
        })
      : supportedTradeMethods;
    const allowedBankIds = new Set(
      uniq(map(tradeMethodsForPaymentType, 'bankId')),
    );
    return filter(formUtils.getOptions(getNotArchived(banks)), (opt) =>
      allowedBankIds.has(opt.value),
    );
  }, [
    banks,
    getNotArchived,
    supportedTradeMethods,
    formik.values.paymentTypeId,
  ]);

  const mappedFiatCurrenciesOptions = useMemo(() => {
    const shop = find(filteredShops, { id: formik.values.shopId });
    if (!shop) {
      return [] as FormikSelectOption[];
    }
    if (shop.fiatCurrencyId) {
      return filter(fiatCurrenciesOptions, { value: shop.fiatCurrencyId });
    }
    return filter(
      fiatCurrenciesOptions,
      (option) =>
        some(tradeMethods, { fiatCurrencyId: option.value }) &&
        some(queryResultCurrencyExchange.data, {
          fiatCurrencyId: option.value,
          assetCurrencyId: shop.assetCurrencyId,
        }),
    );
  }, [
    filteredShops,
    fiatCurrenciesOptions,
    formik.values.shopId,
    queryResultCurrencyExchange.data,
    tradeMethods,
  ]);

  useEffect(() => {
    setFieldValue('paymentTypeId', '', false);
    setFieldValue('bankId', '', false);
    setFieldTouched('fiatCurrencyId', false, false);
    setFieldTouched('paymentTypeId', false, false);
    setFieldTouched('bankId', false, false);
  }, [formik.values.shopId, setFieldValue, setFieldTouched]);

  useEffect(() => {
    if (isEmpty(mappedFiatCurrenciesOptions)) {
      if (formik.values.fiatCurrencyId) {
        setFieldValue('fiatCurrencyId', '', false);
      }
      return;
    }
    if (mappedFiatCurrenciesOptions.length === 1) {
      const onlyValue = mappedFiatCurrenciesOptions[0].value;
      if (formik.values.fiatCurrencyId !== onlyValue) {
        setFieldValue('fiatCurrencyId', onlyValue, false);
      }
      return;
    }
    if (
      formik.values.fiatCurrencyId &&
      !some(mappedFiatCurrenciesOptions, {
        value: formik.values.fiatCurrencyId,
      })
    ) {
      setFieldValue('fiatCurrencyId', '', false);
    }
  }, [
    mappedFiatCurrenciesOptions,
    formik.values.fiatCurrencyId,
    setFieldValue,
  ]);

  const isFiatSelectDisabled = useMemo(
    () => mappedFiatCurrenciesOptions.length === 1,
    [mappedFiatCurrenciesOptions],
  );

  return (
    <FormikProvider value={formik}>
      <Dialog
        open={open}
        title={t('title')}
        data={{ values: formik.values, formikHelpers: formik }}
        okDisabled={!formik.isValid || hasNoPayoutConfig}
        onClose={handleClose}
      >
        {hasNoPayoutConfig && (
          <Alert severity="warning" sx={{ marginBottom: 2 }}>
            {t('payout_not_configured')}
          </Alert>
        )}
        <FormControls>
          <Typography variant="subtitle1">
            {t('sections.order_info')}
          </Typography>
          <ShopSelect
            name="shopId"
            shops={filteredShops}
            required
            disablePortal={false}
          />
          <FormControls row>
            <FormikNumericField
              label={t('fields.amount')}
              name="amount"
              required
              fullWidth
              allowNegative={false}
            />
            <FormikSelect
              label={t('fields.currency')}
              name="fiatCurrencyId"
              required
              fullWidth
              options={mappedFiatCurrenciesOptions}
              noneOption
              disabled={isFiatSelectDisabled}
            />
          </FormControls>
        </FormControls>
        {showPayoutSections && (
          <Fragment>
            <Divider sx={{ margin: '24px 0 12px' }} />
            <FormControls>
              <Typography variant="subtitle1">
                {t('sections.payment_info')}
              </Typography>
              <FormikSelect
                label={t('fields.payment_type')}
                name="paymentTypeId"
                options={paymentTypesOptions}
                noneOption
                required
              />
              <FormikSelect
                label={t('fields.bank')}
                name="bankId"
                options={bankOptions}
                noneOption
                required
              />
            </FormControls>
            <Divider sx={{ margin: '24px 0 12px' }} />
            <FormControls>
              <Typography variant="subtitle1">
                {t('sections.recipient_info')}
              </Typography>
              <FormikTextField
                name="customer.id"
                label={t('fields.id')}
                required
              />
              <FormikTextField name="customer.name" label={t('fields.name')} />
              <FormikTextField
                name="customer.email"
                label={t('fields.email')}
              />
              <FormikTextField
                name="customer.telegram"
                label={t('fields.telegram')}
              />
              <FormFields fields={displayedRequisiteFields} />
            </FormControls>
            <Divider sx={{ margin: '24px 0 12px' }} />
            <FormControls>
              <Typography variant="subtitle1">
                {t('sections.integration')}
              </Typography>
              <FormikTextField
                name="integration.externalOrderId"
                label={t('fields.external_order_id')}
              />
              <FormikTextField
                name="integration.callbackUrl"
                label={t('fields.callback_url')}
              />
              <FormikSelect
                name="integration.callbackMethod"
                label={t('fields.callback_method')}
                noneOption
                options={map(values(WebhookMethod), (method) => ({
                  label: toUpper(method),
                  value: method,
                }))}
              />
            </FormControls>
          </Fragment>
        )}
      </Dialog>
    </FormikProvider>
  );
};
